import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import React, { useState } from 'react';

import { useStorage } from '~Components/Storage';
import Button from '~ComponentsImproved/Button';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useBridgeApi, useDeviceInfo, useDispatch, useSelector } from '~Hooks';
import { Input, ModalContent } from '~Scenes/Handy/styles';
import { addToast } from '~actions';
import { VenueTerminals, VenueTerminalsVariables } from './__queries__';

const Wrapper = styled.div`
  margin-top: 16px;
`;

const Terminal = styled.div`
  margin: 10px 0;
  padding: 8px;
  background: #fafafa;
  border-radius: 3px;
`;

const toggleZettleSettings = () => {
  window.postMessageBridge({
    showIZettleSettings: true,
  });
};

const TERMINALS_QUERY = gql`
  query VenueTerminals($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      adyenTerminals {
        id
        details {
          terminal
          ethernetIp
          wifiIp
        }
      }
    }
  }
`;

const TerminalSetup = () => {
  const venueId = useSelector((state) => state.venueId!);
  const appVersion = useSelector((state) => state.appVersion); // TEMP-ZETTLE-DISABLE
  const dispatch = useDispatch();
  const [{ adyenTerminals }, updateStorage] = useStorage();
  const { isShowing: idInputIsShowing, toggle: toggleIdInput } = useModal();
  const [terminalIdInput, setTerminalIdInput] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const hasAdyen = paymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = paymentMethods.some((method) => method.id === 'IZETTLE');

  const terminals = useQuery<VenueTerminals, VenueTerminalsVariables>(TERMINALS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      venueId,
    },
    skip: !venueId,
  });
  const allTerminals = terminals.data?.venue.adyenTerminals || [];
  const s1F2Terminals = allTerminals.filter((terminal) => terminal.id.includes('S1F2-'));

  const deviceInfo = useDeviceInfo();
  const bridgeApi = useBridgeApi();

  const addAdyenTerminal = () => {
    if (!terminalIdInput) {
      return;
    }

    setLoading(true);

    // Create storage object
    const terminal = {
      POIID: terminalIdInput,
      // If it's an S1F2 terminal, we assume it's a local terminal
      IP: terminalIdInput.includes('S1F2') ? '127.0.0.1' : null,
      hasPrinter: true,
    };

    // Wait 1 seconds before closing modal
    setTimeout(() => {
      // Add terminal to storage (and remove any existing terminals)
      updateStorage({
        adyenTerminals: [terminal],
      });
      toggleIdInput();
      setLoading(false);
      dispatch(addToast('Terminal successfully configured', true));
    }, 1000);
  };

  /**
   * If we dont have device info (outdated client or slow response),
   * we show nothing instead of accidentally showing the wrong setup.
   */
  if (!deviceInfo) {
    return null;
  }

  // If not adyen, we assume it's zettle. Sometimes customers have 0 payment methods, and thats = zettle
  if (hasZettle || (!deviceInfo?.isAdyenTerminal && !hasAdyen)) {
    return (
      <Wrapper>
        <Button
          text="Zettle settings"
          styleType="Confirm"
          onTouchStart={() => toggleZettleSettings()}
        />
        <br />
        {appVersion.charAt(0) !== '4' && ( // TEMP-ZETTLE-DISABLE
          <Button
            text="Tip settings"
            styleType="Confirm"
            onTouchStart={() => bridgeApi.showZettleTipSettings()}
          />
        )}
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper>
        <Button text="Setup Adyen" styleType="Confirm" onTouchStart={toggleIdInput} />
      </Wrapper>
      <Modal isShowing={idInputIsShowing} hide={toggleIdInput}>
        <ModalContent>
          <Heading level={3} color="#fff">
            Enter terminal ID
          </Heading>
          <Paragraph color="#fff" level={5} top={8} bottom={8}>
            Enter the terminal ID of the Adyen terminal you want to use.
            <br /> It can be found on the back of the terminal.
          </Paragraph>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span
              style={{
                display: 'inline-block',
                marginRight: '2px',
                marginTop: '13px',
                color: '#fff',
              }}>
              ID:
            </span>
            <Input
              value={terminalIdInput}
              onChange={(e) => setTerminalIdInput(e.target.value)}
              style={{ padding: '8px 16px 8px 2px' }}
            />
          </div>
          {(deviceInfo?.isAdyenTerminal ? s1F2Terminals : allTerminals).map((terminal) => (
            <Terminal
              key={terminal.id}
              style={{ marginBottom: '8px' }}
              onClick={() => {
                setTerminalIdInput(terminal.id);
              }}>
              {terminal.id}
            </Terminal>
          ))}
          <Button
            text={loading ? 'Configuring...' : 'Add'}
            styleType="Confirm"
            onTouchStart={() => addAdyenTerminal()}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

export default TerminalSetup;
